export interface PrItem {
    /** "{owner}/{repo}#{number}" */
    ref: string;
    title: string;
    url: string;
}

export interface MergedPrItem extends PrItem {
    additions: number;
    deletions: number;
    mergedAt: string | null;
}

export interface IssueItem {
    ref: string;
    title: string;
    url: string;
    createdAt?: string;
    closedAt?: string;
}

export interface DocItem {
    id: string;
    name: string;
    url: string;
    createdAt: string | null;
}

export interface DemoItem {
    id: string;
    title: string;
    /** YYYY-MM-DD. */
    date: string;
    url: string | null;
}

export const EVENT_TYPES = ['missed_sla', 'missed_meeting'] as const;
export type EventType = (typeof EVENT_TYPES)[number];

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
    missed_sla: 'Missed SLA',
    missed_meeting: 'Missed meeting',
};

/** Event embedded inside the Team Insights member row — minimal fields for drill-down rendering. */
export interface MemberEventItem {
    id: string;
    type: string; // EventType, kept as string for forward-compat
    date: string;
    note: string;
}

/** Full event record returned by the events CRUD API. */
export interface TeamMemberEvent {
    id: string;
    login: string;
    type: EventType;
    date: string;
    note: string;
    createdAt: string;
    updatedAt: string;
}

export interface TeamMemberInsight {
    login: string;
    avatarUrl: string;
    prsMerged: number;
    /** Count of distinct PRs the user had at least one in-window action on. */
    prsActedOn: number;
    locChanged: number;
    issuesOpened: number;
    issuesClosed: number;
    /** Count of distinct issues the user had at least one in-window timeline event on. */
    issuesActedOn: number;
    docsCreated: number;
    demos: number;
    events: number;
    /** Total Coder workspace active time in the window, in seconds. Undefined when Coder is off. */
    coderActiveSeconds?: number;
    /** Coder active seconds per template display name, e.g. { 'sonat-base': 28080 }. */
    coderTemplateSeconds?: Record<string, number>;
    // Drill-down item arrays (length matches the count fields above).
    prsMergedItems: MergedPrItem[];
    prsActedOnItems: PrItem[];
    issuesOpenedItems: IssueItem[];
    issuesClosedItems: IssueItem[];
    issuesActedOnItems: IssueItem[];
    docsCreatedItems: DocItem[];
    demosItems: DemoItem[];
    eventsItems: MemberEventItem[];
}

export interface DailyMemberBreakdown {
    login: string;
    prsMerged: number;
    prsActedOn: number;
    locChanged: number;
    issuesOpened: number;
    issuesClosed: number;
    issuesActedOn: number;
    docsCreated: number;
    demos: number;
    /** Per-type event counts, e.g. { missed_sla: 1 }. */
    eventsByType: Record<string, number>;
    /** Coder active workspace seconds this day (total across templates). */
    coderActiveSeconds?: number;
    /** Coder active seconds per template name this day, e.g. { 'sonat-base': 3600 }. */
    coderTemplateSeconds?: Record<string, number>;
}

export interface DailyBreakdown {
    date: string;
    members: DailyMemberBreakdown[];
}

export interface TeamInsightsResponse {
    window: { from: string; to: string };
    org: string;
    truncated: boolean;
    members: TeamMemberInsight[];
    dailyBreakdown: DailyBreakdown[];
    /** Coder template display names to render as usage columns. Empty when Coder is off. */
    coderTemplates: string[];
}

export interface TeamInsightsFilters {
    /** Slug of a preset range (e.g. "current-month"). Mutually exclusive with from/to. */
    preset: string | null;
    from: string | null;
    to: string | null;
}

export interface MonthlyReportSummary {
    login: string;
    avatarUrl: string;
    hasReport: boolean;
    lastEditedAt: string | null;
}

export interface MonthlyReport {
    login: string;
    /** YYYY-MM */
    month: string;
    content: string;
    generatedAt: string | null;
    lastEditedAt: string;
}

export interface GeneratedReport {
    login: string;
    month: string;
    content: string;
    generatedAt: string;
}
